import isNil from 'lodash/isNil';
import { ff } from '@atlassian/jira-feature-flagging';
import { UNSUPPRESS_ALERT_CREATION } from '@atlassian/jira-servicedesk-system-incident-status/src/services/constants.tsx';
import { createHook, createStore, createContainer } from '@atlassian/react-sweet-state';
import { type Actions, actions } from './actions';
import type { State, ContainerProps } from './types';

export const initialState: State = {
	responderAlertsConfiguration: {
		data: undefined,
		error: undefined,
		isLoading: false,
	},
	slackWorkspaceConnections: {
		data: undefined,
		error: undefined,
		isLoading: false,
	},
	pirConfiguration: {
		isSaving: false,
		isLoading: false,
		isEditEnabled: false,
	},
	msTeamsWorkspaceConnections: {
		data: undefined,
		error: undefined,
		isLoading: false,
		isChangeInStatusProgress: false,
		isMsTeamsEnabled: false,
		isConnected: false,
	},
	zoomWorkspaceConnections: {
		data: undefined,
		error: undefined,
		isLoading: false,
		isChangeInStatusProgress: false,
		isZoomEnabled: false,
		isConnected: false,
	},
	chatOpsEntitlementEnabled: true,
};

export const responderAlertsConfigurationStore = createStore<State, Actions>({
	name: 'JSDIncidentManagementResponderAlertsConfiguration',
	initialState,
	actions,
});

export const ResponderAlertsConfigurationContainer = createContainer<
	State,
	Actions,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Record<any, any>
>(responderAlertsConfigurationStore);

export const useShouldSendResponderAlerts = createHook(responderAlertsConfigurationStore, {
	selector: ({ responderAlertsConfiguration }: State) => ({
		shouldSendResponderAlerts: responderAlertsConfiguration.data === UNSUPPRESS_ALERT_CREATION,
		responderAlertsConfigurationData: responderAlertsConfiguration.data,
		error: responderAlertsConfiguration.error,
	}),
});

export const slackWorkspaceConnectionsStore = createStore<State, Actions>({
	name: 'JSDIncidentManagementSlackWorkspaceConnections',
	initialState,
	actions,
});

export const CommunicationsConnectionsContainer = createContainer<State, Actions, ContainerProps>(
	slackWorkspaceConnectionsStore,
	{
		onInit:
			() =>
			({ dispatch }, { projectId }) => {
				dispatch(actions.fetchMsTeamWorkspaceConnectionForProject({ projectId }));
				dispatch(actions.fetchZoomWorkspaceConnectionForProject());
				ff('chatops-pp-banners_1o319') ? dispatch(actions.fetchChatopsEntitlementAction()) : null;
			},
	},
);

export const useSlackWorkspaceConnections = createHook(slackWorkspaceConnectionsStore, {
	selector: ({ slackWorkspaceConnections }: State) => ({
		slackWorkspaceConnectionsData: slackWorkspaceConnections.data,
		error: slackWorkspaceConnections.error,
	}),
});

export const pirConfigurationStore = createStore<State, Actions>({
	name: 'JSDIncidentManagementPIRConfiguration',
	initialState,
	actions,
});

export const PIRSettingsContainer = createContainer<State, Actions, ContainerProps>(
	pirConfigurationStore,
	{
		onInit:
			() =>
			({ dispatch }, { projectId, cloudId, analyticsEvent }) => {
				dispatch(actions.fetchConfluenceConfiguration({ projectId, cloudId, analyticsEvent }));
			},
	},
);

export const usePIRConfiguration = createHook(pirConfigurationStore, {
	selector: ({ pirConfiguration }: State) => ({
		pirConfigurationData: pirConfiguration,
		defaultSpaceValue:
			!isNil(pirConfiguration.selectedConfluenceSpaceId) &&
			!isNil(pirConfiguration.selectedConfluenceSpaceName)
				? {
						label: pirConfiguration.selectedConfluenceSpaceName,
						value: pirConfiguration.selectedConfluenceSpaceId,
					}
				: null,
		defaultPageValue:
			!isNil(pirConfiguration.selectedConfluenceParentPageId) &&
			!isNil(pirConfiguration.selectedConfluenceParentPageName)
				? {
						label: pirConfiguration.selectedConfluenceParentPageName,
						value: pirConfiguration.selectedConfluenceParentPageId,
					}
				: null,
	}),
});

export const useMsTeamsWorkspaceConnections = createHook(slackWorkspaceConnectionsStore, {
	selector: ({ msTeamsWorkspaceConnections }: State) => ({
		msTeamsWorkspaceConnectionsData: msTeamsWorkspaceConnections.data,
		error: msTeamsWorkspaceConnections.error,
		isLoading: msTeamsWorkspaceConnections.isLoading,
	}),
});

export const useMsTeamsConnectionStatusInfo = createHook(slackWorkspaceConnectionsStore, {
	selector: ({ msTeamsWorkspaceConnections }: State) => ({
		isMsTeamsEnabled: msTeamsWorkspaceConnections.isMsTeamsEnabled,
		isConnected: msTeamsWorkspaceConnections.isConnected,
		isChangeInStatusProgress: msTeamsWorkspaceConnections.isChangeInStatusProgress,
	}),
});
export const useZoomWorkspaceConnections = createHook(slackWorkspaceConnectionsStore, {
	selector: ({ zoomWorkspaceConnections }: State) => ({
		zoomWorkspaceConnectionsData: zoomWorkspaceConnections.data,
		error: zoomWorkspaceConnections.error,
		isLoading: zoomWorkspaceConnections.isLoading,
	}),
});

export const useZoomConnectionStatusInfo = createHook(slackWorkspaceConnectionsStore, {
	selector: ({ zoomWorkspaceConnections }: State) => ({
		isZoomEnabled: zoomWorkspaceConnections.isZoomEnabled,
		isConnected: zoomWorkspaceConnections.isConnected,
		isChangeInStatusProgress: zoomWorkspaceConnections.isChangeInStatusProgress,
	}),
});

export const useChatOpsEntitlement = createHook(slackWorkspaceConnectionsStore, {
	selector: ({ chatOpsEntitlementEnabled }: State) => ({
		chatOpsEntitlementEnabled,
	}),
});
